import React, { useState, useEffect } from 'react';
import {
  CheckCircle2, AlertCircle, Circle, CalendarDays, MapPin, Building2,
  FileSignature, Users, BookOpen, UserCheck, FileText, ClipboardCheck,
  Award, ScrollText, Mail, TrendingUp, Hash, User, Bell
} from 'lucide-react';

// Status icon component
const StatusIcon = ({ status }) => {
  if (status === 'done') return <CheckCircle2 className="h-5 w-5 text-indigo-500 shrink-0" />;
  if (status === 'warning') return <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />;
  return <Circle className="h-5 w-5 text-slate-300 shrink-0" />;
};

// Single progress item with vertical timeline
const ProgressItem = ({ title, status, icon: Icon, children, isLast }) => (
  <div className="flex gap-3">
    <div className="flex flex-col items-center">
      <StatusIcon status={status} />
      {!isLast && <div className={`w-0.5 flex-1 min-h-[16px] ${status === 'done' ? 'bg-indigo-300' : status === 'warning' ? 'bg-red-300' : 'bg-slate-200'}`} />}
    </div>
    <div className="flex-1 pb-4">
      <p className={`text-sm font-semibold mb-1 ${status === 'warning' ? 'text-red-600' : status === 'done' ? 'text-indigo-600' : 'text-slate-500'}`}>
        {title}
      </p>
      {children && (
        <div className={`rounded-lg p-2.5 text-xs space-y-1 ${
          status === 'warning' ? 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800' :
          'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800'
        }`}>
          {children}
        </div>
      )}
    </div>
  </div>
);

// Column header with gradient
const ColumnHeader = ({ gradient, icon: Icon, progress, total }) => (
  <div className={`relative rounded-xl p-4 h-28 flex items-end overflow-hidden ${gradient}`}>
    <div className="absolute inset-0 flex items-center justify-center opacity-20">
      <Icon className="h-16 w-16 text-white" />
    </div>
    <div className="absolute bottom-3 left-3">
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
        progress === 0 ? 'bg-red-500 text-white' : progress < total ? 'bg-orange-500 text-white' : 'bg-emerald-500 text-white'
      }`}>
        <AlertCircle className="h-3 w-3" />
        {progress} / {total}
      </div>
    </div>
  </div>
);

// KPI item for summary bar
const KpiItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="w-8 h-8 rounded-full bg-white/80 dark:bg-slate-700 flex items-center justify-center shadow-sm">
      <Icon className="h-4 w-4 text-indigo-500" />
    </div>
    <div>
      <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-bold text-slate-800 dark:text-white">{value || '—'}</p>
    </div>
  </div>
);

// Compute progress for each column
const computeProgress = (form) => {
  const nbApprenants = form.apprenants?.length || 0;
  const nbIntervenants = form.intervenants?.length || 0;
  const hasDates = !!(form.start_date && form.end_date);
  const hasLieu = !!(form.formation_a_distance || form.lieu_formation || form.location);
  const hasProgramme = !!form.programme_id;
  const hasDescription = !!form.description;
  const nbCreneaux = form.creneaux?.length || 0;

  // Column 1: Dates
  const datesItems = [
    { done: hasDates },
    { done: hasLieu },
    { done: true }, // Clients placeholder
    { done: false }, // Devis - no system yet
    { done: nbApprenants > 0 },
    { done: hasProgramme },
    { done: nbIntervenants > 0 },
  ];
  const datesProgress = datesItems.filter(i => i.done).length;

  // Column 2: Gestion
  const gestionProgress = 0; // No document system yet
  const gestionTotal = 4;

  // Column 3: Configuration
  const configItems = [
    { done: hasDescription },
    { done: false }, // Image
    { done: nbApprenants > 0 }, // Accès
  ];
  const configProgress = configItems.filter(i => i.done).length;

  // Column 4: Émargements
  const emargProgress = 0;
  const emargTotal = 3;

  return {
    dates: { progress: datesProgress, total: datesItems.length },
    gestion: { progress: gestionProgress, total: gestionTotal },
    config: { progress: configProgress, total: configItems.length },
    emarg: { progress: emargProgress, total: emargTotal },
    total: datesProgress + gestionProgress + configProgress + emargProgress,
    totalMax: datesItems.length + gestionTotal + configItems.length + emargTotal,
    nbApprenants,
    nbIntervenants,
    hasDates,
    hasLieu,
    hasProgramme,
    hasDescription,
    nbCreneaux,
  };
};

const formatPrice = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SessionAvancementTab = ({ form, isEditMode, sessionId }) => {
  const p = computeProgress(form);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (isEditMode && sessionId) {
      fetch(`${API}/admin/sessions/${sessionId}/alerts`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setAlerts(data.alerts || []); })
        .catch(() => {});
    }
  }, [isEditMode, sessionId]);

  if (!isEditMode) {
    return (
      <div className="text-center py-12 text-slate-400" data-testid="tab-avancement-content">
        <ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Enregistrez d'abord la session pour accéder au tableau d'avancement.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="tab-avancement-content">
      {/* Alerts banner */}
      {alerts.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4" data-testid="avancement-alerts-banner">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="h-5 w-5 text-red-500" />
            <h4 className="text-sm font-bold text-red-700 dark:text-red-400">{alerts.length} alerte{alerts.length > 1 ? 's' : ''} en cours</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {alerts.map((alert, i) => (
              <div key={i} className="flex items-start gap-2 bg-white dark:bg-slate-800 rounded-lg p-2.5 border border-red-100 dark:border-red-900">
                <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-red-700 dark:text-red-400">{alert.title}</p>
                  <p className="text-[10px] text-red-500 dark:text-red-400/70">{alert.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Summary bar */}
      <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-indigo-900/20 rounded-xl p-4 flex flex-wrap items-center justify-around gap-4 border border-indigo-100 dark:border-indigo-800" data-testid="avancement-summary-bar">
        <KpiItem icon={TrendingUp} label="Chiffre d'affaires" value={formatPrice(form.pricing_enterprise)} />
        <KpiItem icon={TrendingUp} label="Taux de marge" value="0%" />
        <KpiItem icon={Hash} label="Code interne" value={form.code_interne || '—'} />
        <KpiItem icon={User} label="Gestionnaire" value={form.gestionnaire_1 || '—'} />
      </div>

      {/* Overall progress */}
      <div className="flex justify-center" data-testid="avancement-progress-badge">
        <div className="bg-indigo-600 text-white px-5 py-1.5 rounded-full text-sm font-bold shadow-lg shadow-indigo-200 dark:shadow-indigo-900">
          {p.total} / {p.totalMax}
        </div>
      </div>

      {/* 4 progress columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="avancement-columns">
        {/* Column 1: Dates */}
        <div className="space-y-3">
          <ColumnHeader gradient="bg-gradient-to-br from-indigo-500 to-purple-500" icon={CalendarDays} progress={p.dates.progress} total={p.dates.total} />
          <div className="pl-1">
            <ProgressItem title="Dates" status={p.hasDates ? 'done' : 'warning'}>
              {p.hasDates ? (
                <>
                  <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                    <CalendarDays className="h-3 w-3" />
                    {p.nbCreneaux > 0 ? `${p.nbCreneaux} créneaux définis` : 'Horaires définis'}
                  </div>
                  <p className="text-slate-500 dark:text-slate-400">Du {form.start_date} au {form.end_date}</p>
                </>
              ) : <p className="text-red-500">Dates non définies</p>}
            </ProgressItem>
            <ProgressItem title="Lieux de formation" status={p.hasLieu ? 'done' : 'warning'}>
              <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <MapPin className="h-3 w-3" />
                {form.formation_a_distance ? 'À distance' : (form.lieu_formation || form.location || 'Non défini')}
              </div>
            </ProgressItem>
            <ProgressItem title="Clients" status="done">
              <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <Building2 className="h-3 w-3" /> 1 entreprise
              </div>
            </ProgressItem>
            <ProgressItem title="Devis" status="warning">
              <p className="text-slate-500 dark:text-slate-400">Envoyés par e-mail (0/1)</p>
              <p className="text-slate-500 dark:text-slate-400">Signés (0/1)</p>
            </ProgressItem>
            <ProgressItem title="Apprenants" status={p.nbApprenants > 0 ? 'done' : 'warning'}>
              <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <Users className="h-3 w-3" /> {p.nbApprenants} apprenants nommés
              </div>
              <p className="text-slate-500 dark:text-slate-400">0 apprenant déclaré</p>
            </ProgressItem>
            <ProgressItem title="Programme" status={p.hasProgramme ? 'done' : 'warning'}>
              {p.hasProgramme ? (
                <div className="text-slate-600 dark:text-slate-300">
                  <p className="text-indigo-500 text-[10px] uppercase">Programme lié</p>
                  <p className="font-medium">{form.course_title || form.title}</p>
                </div>
              ) : <p className="text-red-500">Aucun programme lié</p>}
            </ProgressItem>
            <ProgressItem title="Intervenants" status={p.nbIntervenants > 0 ? 'done' : 'warning'} isLast>
              {p.nbIntervenants > 0 ? (
                form.intervenants.map((interv, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <UserCheck className="h-3 w-3" />
                    <span>{interv.name}</span>
                    <span className="bg-indigo-500 text-white text-[9px] px-1.5 py-0.5 rounded font-bold">Interne</span>
                  </div>
                ))
              ) : <p className="text-red-500">Aucun intervenant</p>}
            </ProgressItem>
          </div>
        </div>

        {/* Column 2: Gestion (Conventions) */}
        <div className="space-y-3">
          <ColumnHeader gradient="bg-gradient-to-br from-teal-400 to-emerald-500" icon={FileText} progress={p.gestion.progress} total={p.gestion.total} />
          <div className="pl-1">
            <ProgressItem title="Conventions" status="warning">
              <p className="text-slate-500 dark:text-slate-400">Documents générés (0/1)</p>
              <p className="text-slate-500 dark:text-slate-400">Envoyés par e-mail (0/1)</p>
              <p className="text-slate-500 dark:text-slate-400">Signés (0/1)</p>
            </ProgressItem>
            <ProgressItem title="Convocations" status="done">
              <p className="text-slate-500 dark:text-slate-400">Documents générés ({p.nbApprenants}/{p.nbApprenants || 1})</p>
              <p className="text-slate-500 dark:text-slate-400">Envoyés par e-mail ({p.nbApprenants}/{p.nbApprenants || 1})</p>
              <p className="text-emerald-600 dark:text-emerald-400 text-[10px]">Envoi automatique activé</p>
            </ProgressItem>
            <ProgressItem title="Évaluations" status="pending">
              <p className="text-slate-400 italic">Aucune évaluation pour le moment</p>
            </ProgressItem>
            <ProgressItem title="Factures" status="pending" isLast>
              <p className="text-slate-400 italic">Aucune facture pour le moment</p>
            </ProgressItem>
          </div>
        </div>

        {/* Column 3: Configuration */}
        <div className="space-y-3">
          <ColumnHeader gradient="bg-gradient-to-br from-cyan-400 to-teal-500" icon={BookOpen} progress={p.config.progress} total={p.config.total} />
          <div className="pl-1">
            <ProgressItem title="Configuration" status={p.hasDescription ? 'done' : 'warning'}>
              <div className="flex items-center gap-2">
                {p.hasDescription ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <AlertCircle className="h-3 w-3 text-red-500" />}
                <span className="text-slate-600 dark:text-slate-300">Description</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-3 w-3 text-red-500" />
                <span className="text-slate-600 dark:text-slate-300">Image</span>
              </div>
            </ProgressItem>
            <ProgressItem title="Accès" status={p.nbApprenants > 0 ? 'warning' : 'pending'}>
              <p className="text-slate-500 dark:text-slate-400">
                Apprenants ayant ouvert l'Espace Apprenant ({Math.max(0, p.nbApprenants - 1)}/{p.nbApprenants || 1})
              </p>
            </ProgressItem>
            <ProgressItem title="Séquences e-learning" status="done" isLast>
              <p className="text-slate-600 dark:text-slate-300">Séquences créées (0)</p>
            </ProgressItem>
          </div>
        </div>

        {/* Column 4: Émargements */}
        <div className="space-y-3">
          <ColumnHeader gradient="bg-gradient-to-br from-amber-400 to-orange-500" icon={ClipboardCheck} progress={p.emarg.progress} total={p.emarg.total} />
          <div className="pl-1">
            <ProgressItem title="Émargements" status="warning">
              <p className="text-slate-500 dark:text-slate-400">Apprenants ({0}/{p.nbApprenants || 0})</p>
              <p className="text-slate-500 dark:text-slate-400">Intervenants ({0}/{p.nbIntervenants || 0})</p>
            </ProgressItem>
            <ProgressItem title="Certificats" status="warning">
              <p className="text-slate-500 dark:text-slate-400">Documents générés (0/{p.nbApprenants || 0})</p>
              <p className="text-slate-500 dark:text-slate-400">Envoyés par e-mail (0/{p.nbApprenants || 0})</p>
            </ProgressItem>
            <ProgressItem title="Attestations" status="warning" isLast>
              <p className="text-slate-500 dark:text-slate-400">Documents générés (0/{p.nbApprenants || 0})</p>
              <p className="text-slate-500 dark:text-slate-400">Envoyés par e-mail (0/{p.nbApprenants || 0})</p>
            </ProgressItem>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionAvancementTab;
